import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import i18n from "../i18n";
import {
  SUPPORTED_LANGUAGE_CODES,
  normalizeLanguageCode,
} from "../constants/supportedLanguages";
import { baseUrl } from "../apis/util";

const LanguageContext = createContext();

export { SUPPORTED_LANGUAGE_CODES, normalizeLanguageCode };

export const LanguageProvider = ({ children }) => {
  const [userLanguage, setUserLanguage] = useState(
    normalizeLanguageCode(i18n.language) || "da"
  );
  const [isLoading, setIsLoading] = useState(false);
  const userChangedLanguageRef = useRef(false);
  const [availableLanguages] = useState([
    { code: "bg", name: "Български", flag: "🇧🇬" },
    { code: "cs", name: "Čeština", flag: "🇨🇿" },
    { code: "da", name: "Dansk", flag: "🇩🇰" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "el", name: "Ελληνικά", flag: "🇬🇷" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "et", name: "Eesti", flag: "🇪🇪" },
    { code: "fi", name: "Suomi", flag: "🇫🇮" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "ga", name: "Gaeilge", flag: "🇮🇪" },
    { code: "hr", name: "Hrvatski", flag: "🇭🇷" },
    { code: "hu", name: "Magyar", flag: "🇭🇺" },
    { code: "id", name: "Bahasa Indonesia", flag: "🇮🇩" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
    { code: "lt", name: "Lietuvių", flag: "🇱🇹" },
    { code: "lv", name: "Latviešu", flag: "🇱🇻" },
    { code: "mt", name: "Malti", flag: "🇲🇹" },
    { code: "nl", name: "Nederlands", flag: "🇳🇱" },
    { code: "pl", name: "Polski", flag: "🇵🇱" },
    { code: "pt", name: "Português", flag: "🇵🇹" },
    { code: "ro", name: "Română", flag: "🇷🇴" },
    { code: "sk", name: "Slovenčina", flag: "🇸🇰" },
    { code: "sl", name: "Slovenščina", flag: "🇸🇮" },
    { code: "sv", name: "Svenska", flag: "🇸🇪" },
  ]);

  // Load user's language preference on app start
  useEffect(() => {
    loadUserLanguagePreference();
  }, []);

  // Listen for language changes from i18n
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setUserLanguage(normalizeLanguageCode(lng) || "da");
    };

    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  const applyLanguage = (languageCode) => {
    const normalized = normalizeLanguageCode(languageCode);
    if (!normalized) return;
    setUserLanguage(normalized);
    localStorage.setItem("preferredLanguage", normalized);
    if (normalizeLanguageCode(i18n.language) !== normalized) {
      i18n.changeLanguage(normalized);
    }
  };

  const loadUserLanguagePreference = async () => {
    const localPref = normalizeLanguageCode(
      localStorage.getItem("preferredLanguage")
    );
    if (localPref) {
      applyLanguage(localPref);
    }

    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const response = await fetch(`${baseUrl}/api/accounts/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) return;

      const payload = await response.json();
      const userData = payload?.data || payload;
      const pref = normalizeLanguageCode(
        userData.preferred_language || userData.language
      );

      // Don't clobber a language the user just picked in this session
      if (!pref || userChangedLanguageRef.current) return;

      const currentLocal = normalizeLanguageCode(
        localStorage.getItem("preferredLanguage")
      );
      // Prefer the freshest local choice when it already differs from server
      if (currentLocal && currentLocal !== pref && localPref) {
        return;
      }

      applyLanguage(pref);
    } catch (error) {
      console.error("Failed to load language preference:", error);
    }
  };

  const updateLanguagePreference = async (languageCode) => {
    const normalized = normalizeLanguageCode(languageCode) || "en";
    setIsLoading(true);
    userChangedLanguageRef.current = true;
    try {
      applyLanguage(normalized);

      const token = localStorage.getItem("access_token");
      if (token) {
        const response = await fetch(
          `${baseUrl}/api/accounts/language-preference/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ language: normalized }),
          }
        );

        if (!response.ok) {
          console.warn("Failed to update language preference on server");
        }
      }
    } catch (error) {
      console.error("Error updating language preference:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getLanguageInfo = (code) => {
    return (
      availableLanguages.find((lang) => lang.code === code) ||
      availableLanguages[0]
    );
  };

  return (
    <LanguageContext.Provider
      value={{
        userLanguage,
        availableLanguages,
        updateLanguagePreference,
        isLoading,
        getLanguageInfo,
        // Backward compatibility with existing LocaleContext
        locale: userLanguage,
        setLocale: updateLanguagePreference,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export default LanguageContext;
